import Swiper, { SwiperSlide } from '@/components/common/Swiper';
import { twMerge } from 'tailwind-merge';
import FilterListSkeleton from '@/components/vendors/FilterListSkeleton';
import { useState, useRef } from 'react';
import ImpTracker from '@/components/common/ImpTracker';
import useClickActionT2 from '@/tracks/useClickActionT2';
import useImpressionT2 from '@/tracks/useImpressionT2';
import classNames from 'classnames';
import useSticky from '@/hooks/useSticky';
import useGlobalNavHeader from '@/hooks/globalnavheader/provider';

type Props = {
  filterData?: any[];
  onClickTab?: (key: string, cb?: () => void) => void;
  componentPos?: number;
  stickyTop?: number;
  scrollHeight?: number;
  stickyFilter?: boolean;
  pageTheme?: Record<string, any>;
  testIdPrefix?: string;
};

const HAVE_WE_FIXED_STREAMLINED_NAVHEADER_STICKY = false;

const LandingFilter: React.FC<Props> = ({
  filterData,
  onClickTab,
  componentPos,
  stickyTop = 47,
  scrollHeight = 47,
  stickyFilter = true,
  pageTheme,
  testIdPrefix
}: Props) => {
  const { filterBtnImpression } = useImpressionT2();
  const { filterBtnClick } = useClickActionT2();
  const [selected, setSlelected] = useState<ObjectType>({
    key: 'feature',
    index: 0
  });

  const globalNavHeader = useGlobalNavHeader();
  const streamlinedHeader = globalNavHeader?.streamlinedHeader || false;
  const skipSubSectionStickyForRoute = HAVE_WE_FIXED_STREAMLINED_NAVHEADER_STICKY ? false : streamlinedHeader;

  //是否悬浮过
  const isStickyEmit = useRef<boolean>(false);
  const { eleRef } = useSticky({
    scrollHeight: scrollHeight,
    skipSubSectionStickyForRoute: skipSubSectionStickyForRoute,
    cb: (sticky, entry) => {
      if (!stickyFilter) return;
      const recommendTabFixedDom = document.querySelector('#categoryFilter');
      const searchBarDom = document.getElementById('homepage-search-bar');
      const searchInput = document.getElementById('homepage-search-bar-input');
      const bakeryHeader = document.getElementById('bakery-landing-header');
      if (entry?.boundingClientRect?.top > window.innerHeight) return;
      if (sticky) {
        recommendTabFixedDom?.classList.add('tabFixedShow');
        if (!!searchBarDom) {
          searchBarDom.style.boxShadow = '';
        }
        if (!!searchInput) {
          searchInput.style.boxShadow = '';
        }
        if (!!bakeryHeader) {
          bakeryHeader.style.boxShadow = '';
        }
        isStickyEmit.current = true;
      } else {
        recommendTabFixedDom?.classList.remove('tabFixedShow');
        if (isStickyEmit.current && searchBarDom) {
          searchBarDom.style.boxShadow = '0px 4px 4px 0px rgba(0, 0, 0, 0.04)';
        }
        if (!!searchInput) {
          searchInput.style.boxShadow = '0px 9px 20px rgba(0, 0, 0, 0.07), 0px 1px 8px rgba(0, 0, 0, 0.04)';
        }
        if (!!bakeryHeader) {
          bakeryHeader.style.boxShadow = '0px 4px 12px 0px rgba(0, 0, 0, 0.12), 0px 0px 3px 0px rgba(0, 0, 0, 0.05)';
        }
      }
    }
  });

  return (
    <div>
      <div ref={eleRef} />
      <div className="h-11 w-full bg-surface-100-bg max-w-[750px] left-0 right-0 mx-auto overflow-hidden">
        <style jsx>{`
          #categoryFilter.tabFixedShow {
            width: 100vw;
            box-sizing: border-box;
            position: fixed;
            top: ${stickyTop}px;
            z-index: 11;
            height: 44px;
            background-color: white;
            box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.05), 0px 4px 12px rgba(0, 0, 0, 0.12);
            border-bottom: none;
          }
        `}</style>
        <div id="categoryFilter" className="w-full h-11 border-b border-solid border-surface-100-hairline">
          {!!filterData && !!filterData?.length ? (
            <Swiper activeIndex={selected.index} spaceBetween={0} slidesPerView="auto" className="!px-5">
              {filterData.map((item, index) => {
                return (
                  <SwiperSlide
                    data-testid={!!testIdPrefix ? `${testIdPrefix}-${item?.key}` : undefined}
                    key={`${index}-${item.label}`}
                    className="inline-block w-unset"
                    style={{ width: 'auto' }}>
                    <ImpTracker
                      impTrackingT2={() => {
                        filterBtnImpression({
                          mod_nm: 'filter_buttons',
                          mod_pos: componentPos,
                          co: {
                            button_nm: item?.key,
                            button_pos: index,
                            is_selected: selected.key === item.key ? true : false
                          },
                          ctx: {
                            filter_sub_category: selected?.key
                          }
                        });
                      }}
                      clickTrackingT2={() => {
                        filterBtnClick({
                          mod_nm: 'filter_buttons',
                          mod_pos: componentPos,
                          co: {
                            button_nm: item?.key,
                            button_pos: index,
                            target_type: 'filter_buttons',
                            click_type: 'view'
                          },
                          ctx: {
                            filter_sub_category: selected?.key
                          }
                        });
                      }}>
                      <div
                        className={twMerge(
                          'enki-body-base-medium px-3 py-2 cursor-pointer text-btn-disabled-fg-default',
                          'border-0 border-b-[2px] border-solid border-[transparent]',
                          classNames({
                            'border-btn-secondary-bg text-btn-secondary-bg': selected.key === item.key,
                            'opacity-60': !!pageTheme?.title_color,
                            'opacity-100': !!pageTheme?.title_color && selected.key === item.key
                          })
                        )}
                        style={{
                          color: pageTheme?.title_color,
                          borderBottomColor: selected.key === item.key ? pageTheme?.title_color : void 0
                        }}
                        onClick={e => {
                          onClickTab?.(item.key, () => {
                            setSlelected({ key: item.key, index });
                          });
                        }}>
                        {item.title}
                      </div>
                    </ImpTracker>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          ) : (
            <FilterListSkeleton />
          )}
        </div>
      </div>
      <div id="componentsContainer"></div>
    </div>
  );
};

export default LandingFilter;
